import {
  defineComponent,
  h,
  inject,
  provide,
  ref,
  type Ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue'

interface Item {
  id: number
  value: unknown
  selected: boolean
}

interface State {
  // Input
  inputEl: Ref<HTMLInputElement | null>
  hideCursor: Ref<boolean>

  // Selected items
  items: Ref<Item[]>
  addItem(value: unknown): void
  removeItem(id: number): void
  selectItem(id: number): void
  selectPreviousItem(): void
  selectNextItem(): void
  getSelectedItem(): Item | undefined
  clearSelection(): void

  // Results
  focusedResultId: Ref<number>
}

const ComboboxContext = Symbol('ComboboxContext')

const useComboboxContext = (component: string): State => {
  const context = inject(ComboboxContext, null)

  if (context === null) {
    throw new Error(`<${component}> is missing parent <Combobox /> component`)
  }

  return context
}
let itemId = -1
let resultId = -1

export type ComboboxType = InstanceType<typeof Combobox> & {
  reset: () => void
}

export const Combobox = defineComponent({
  name: 'Combobox',
  props: ['modelValue'],
  emits: ['update:modelValue'],
  setup(_, { slots, emit, expose }) {
    const api: State = {
      inputEl: ref(null),
      hideCursor: ref(false),
      items: ref([]),
      addItem(value) {
        const item: Item = {
          id: ++itemId,
          value,
          selected: false,
        }

        api.items.value.push(item)

        emit(
          'update:modelValue',
          api.items.value.map((item) => item.value)
        )

        api.focusedResultId.value = -1
        resultId = -1

        if (!api.inputEl.value) return

        api.inputEl.value.value = ''
        api.inputEl.value.dispatchEvent(new Event('keyup'))
        api.inputEl.value.focus()
      },
      removeItem(id) {
        api.items.value = api.items.value.filter((item) => item.id !== id)

        emit(
          'update:modelValue',
          api.items.value.map((item) => item.value)
        )
      },
      selectItem(id) {
        api.items.value.forEach((item) => {
          item.selected = item.id === id
        })
      },
      selectPreviousItem() {
        const index = api.items.value.findIndex(
          (item) => item.id === api.getSelectedItem()?.id
        )

        if (index === -1 || !index) return

        api.selectItem(api.items.value[index - 1].id)
      },
      selectNextItem() {
        const index = api.items.value.findIndex(
          (item) => item.id === api.getSelectedItem()?.id
        )

        if (index === -1 || index === api.items.value.length - 1) return

        api.selectItem(api.items.value[index + 1].id)
      },
      getSelectedItem() {
        return api.items.value.find((item) => item.selected)
      },
      clearSelection() {
        api.items.value.forEach((item) => {
          item.selected = false
        })
      },
      focusedResultId: ref(-1),
    }

    expose({
      reset: () => {
        api.items.value = []
      },
    })

    provide(ComboboxContext, api)

    return () =>
      h(
        'div',
        {
          id: 'combobox',
        },
        slots.default?.({
          addItem: api.addItem,
        })
      )
  },
})

export const ComboboxItem = defineComponent({
  name: 'ComboboxItem',
  setup(_, { slots }) {
    const api = useComboboxContext('ComboboxItem')
    const id = itemId

    return () =>
      h(
        'div',
        {
          onClick() {
            api.selectItem(id)
            api.inputEl.value?.focus()
            api.hideCursor.value = true
          },
        },
        slots.default?.({
          selected: api.getSelectedItem()?.id === id,
        })
      )
  },
})

export const ComboboxInput = defineComponent({
  name: 'ComboboxInput',
  props: {
    placeholder: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ['change'],
  setup(props, { emit }) {
    const api = useComboboxContext('ComboboxInput')

    const handleKeydown = (event: KeyboardEvent) => {
      if (api.getSelectedItem()) {
        event.preventDefault()
        api.hideCursor.value = true
      }

      if (event.key === 'Backspace') {
        if (!api.items.value.length) return

        const cursorPosition = api.inputEl.value?.selectionStart

        if (cursorPosition) return

        if (api.getSelectedItem()) {
          api.removeItem(api.getSelectedItem()!.id)
          api.clearSelection()
          api.hideCursor.value = false
          api.inputEl.value?.focus()
          return
        }

        api.selectItem(api.items.value.at(-1)!.id)
      }

      if (event.key === 'ArrowLeft') {
        if (!api.items.value.length) return

        const cursorPosition = api.inputEl.value?.selectionStart

        if (cursorPosition) return

        if (api.getSelectedItem()) {
          api.selectPreviousItem()
        } else {
          api.selectItem(api.items.value.at(-1)!.id)
          api.hideCursor.value = true
        }
      }

      if (event.key === 'ArrowRight') {
        if (!api.items.value.length) return

        const cursorPosition = api.inputEl.value?.selectionStart

        if (cursorPosition) return

        if (api.getSelectedItem()!.id === api.items.value.at(-1)!.id) {
          api.clearSelection()
          api.hideCursor.value = false
        } else {
          api.selectNextItem()
        }
      }

      if (event.key === 'ArrowDown') {
        api.hideCursor.value = true

        const nextResultId = Math.min(++api.focusedResultId.value, resultId)

        if (document.querySelector(`[tabindex="${nextResultId}"]`)) {
          api.focusedResultId.value = nextResultId
        } else {
          const minTabindex = Math.min(
            ...Array.from(
              document.querySelectorAll('#combobox div[tabindex]')
            ).map((el) => (el as HTMLElement).tabIndex)
          )

          api.focusedResultId.value = minTabindex
        }
      }

      if (event.key === 'ArrowUp') {
        const nextResultId = Math.max(--api.focusedResultId.value, 0)

        if (document.querySelector(`[tabindex="${nextResultId}"]`)) {
          api.focusedResultId.value = nextResultId
        }
      }
    }

    const handleKeyup = (event: KeyboardEvent) => {
      const value = (event.target as HTMLInputElement).value

      emit('change', value)
    }

    const handleClick = () => {
      api.clearSelection()
      api.hideCursor.value = false
    }

    const styles = computed(() => {
      return {
        ...((props.disabled || api.hideCursor.value) && {
          'caret-color': 'transparent',
        }),
      }
    })

    return () =>
      h('input', {
        placeholder: props.placeholder,
        onKeydown: handleKeydown,
        onKeyup: handleKeyup,
        onClick: handleClick,
        ref: api.inputEl,
        style: styles.value,
      })
  },
})

export const ComboboxResult = defineComponent({
  name: 'ComboboxResult',
  props: ['value'],
  setup(props, { slots }) {
    const api = useComboboxContext('ComboboxResult')
    const id = ++resultId
    const el = ref<HTMLElement>()

    watch(
      () => api.focusedResultId,
      () => {
        if (id !== api.focusedResultId.value) {
          return
        }

        el.value?.scrollIntoView({
          block: 'nearest',
        })
      },
      {
        deep: true,
      }
    )

    const onClick = () => {
      api.addItem(props.value)

      api.hideCursor.value = false
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && id === api.focusedResultId.value) {
        api.addItem(props.value)

        api.hideCursor.value = false
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', handleKeydown)
    })

    return () =>
      h(
        'div',
        {
          onClick,
          ref: el,
          tabindex: id,
        },
        slots.default?.({
          focused: id === api.focusedResultId.value,
        })
      )
  },
})
